/* assets/ws-consent.js (2026-10-08 PST). Loaded on every page by snippets/ws-consent.liquid (rendered at the end of layout/theme.liquid).
   LAST UPDATED: 2026-10-08 PST - first version (privacy check, gap "the opt out must stop tracking").

   WHAT IT IS
   The one place where the THEME decides if a tracking tool may run. A tracking tool is a script from another company that records what a
   visitor does (example: Microsoft Clarity records how a visitor moves on a page). The visitor's privacy choice is kept by Shopify.
   This file reads that choice through Shopify's Customer Privacy API (window.Shopify.customerPrivacy) and starts or stops each tool.

   THE RULES (each one costs the shopper nothing: no pop-up, no extra click)
     analytics   allowed when Shopify says analyticsProcessingAllowed().
     saleOfData  allowed when Shopify says saleOfDataAllowed() AND the browser does not send Global Privacy Control
                 (navigator.globalPrivacyControl === true is an opt-out of sale or sharing, always).
     marketing   allowed when Shopify says marketingAllowed() AND saleOfData is allowed.
     FAIL CLOSED when Shopify's API is not on the page or does not answer, nothing is allowed. No tool loads.
     NO RELOAD   Shopify sends the page event "visitorConsentCollected" when a choice changes. This file checks again and stops a running tool.

   TOOLS THE THEME LOADS TODAY: one, Microsoft Clarity (project id from the theme setting "Microsoft Clarity project id").
     Clarity needs analytics AND saleOfData. Reason: Clarity sends the recording to Microsoft, another company. An opted-out visitor is not recorded.
     When the choice changes to "no" while Clarity runs: this file calls Clarity's own consent API (consentv2 "denied", consent false) so Clarity
     erases its cookies, then stops the recording (clarity('stop')). Clarity starts again only on a later page, when the choice allows it.
     Clarity is added after the "load" event, when the browser is idle, so it never slows the page.
   Klaviyo, Yotpo, Intelligems and Re:amaze are app embeds and the ad pixels (Meta, Google, TikTok, Pinterest, Snapchat, Bing) are Shopify pixels.
   The theme does not load them and cannot stop them: docs/legal/PRIVACY_ACTION_PLAN.md lists the admin steps.

   TRIED AND REMOVED (2026-10-08 PST): two vendors document a cookie that should stop their script: "_uetmsdns=1" (Microsoft Advertising, Bing)
   and "__kla_off=true" (Klaviyo). A test on the live store with both cookies set showed no change: the same Klaviyo tracking requests and the
   Bing requests still went out. This file therefore does not set them. Do not add them again without a new test.

   WASHINGTON AND NEVADA (theme setting "Hold back ad sharing for Washington and Nevada visitors", data-holdback-regions on the script tag)
     Both states have a consumer health data law. When the setting is on and Shopify places the visitor in one of these states (getRegion()
     "USWA" or "USNV") and the visitor has made no choice yet, this file records "do not sell or share" for the visitor in Shopify
     (setTrackingConsent({ sale_of_data: false })). It is the same state as a click on "Opt out" on /pages/data-sharing-opt-out. The shopper sees
     nothing. Shopify then holds back the ad pixels that follow its consent. A choice the visitor already made is never changed.
     KNOWN LIMIT (tested 2026-10-08 PST): Shopify starts the ad pixels before any theme script can run. The visitor's FIRST page view still
     reaches them. From the second page on, Meta, TikTok, Pinterest and OpenAI get nothing and Google runs in its restricted mode.

   FOR OTHER THEME CODE: window.wsConsent
     wsConsent.state()                      -> { ready, gpc, region, analytics, marketing, saleOfData, heldBack }
     wsConsent.register(name, needs, start, stop)   needs: a list from 'analytics', 'marketing', 'saleOfData'. start() runs when all are allowed,
                                            stop() runs when one is no longer allowed. Use this for every new tracking tool in the theme.
     Page event "ws:consent" (on document, detail = state) after each check.

   VERSION HISTORY: 2026-10-08 PST first version (replaces the Clarity script that was inline in layout/theme.liquid). */
(function () {
  'use strict';
  if (window.wsConsent) return;

  var tag = document.currentScript;
  var clarityId = tag ? (tag.getAttribute('data-clarity-id') || '') : '';
  var holdRegions = tag ? (tag.getAttribute('data-holdback-regions') || '').split(',').filter(Boolean) : [];

  var cp = null;        /* window.Shopify.customerPrivacy, when it is on the page */
  var ready = false;    /* true after Shopify's API answered */
  var heldBack = false; /* true when this page recorded the opt-out for a Washington or Nevada visitor */
  var tools = [];

  function gpc() { return navigator.globalPrivacyControl === true; }
  function ask(name) {
    try { return !!cp && typeof cp[name] === 'function' && cp[name]() === true; } catch (e) { return false; }
  }
  function region() {
    try { return cp && typeof cp.getRegion === 'function' ? String(cp.getRegion() || '') : ''; } catch (e) { return ''; }
  }
  function state() {
    var sale = ready && !gpc() && ask('saleOfDataAllowed');
    return {
      ready: ready,
      gpc: gpc(),
      region: region(),
      analytics: ready && ask('analyticsProcessingAllowed'),
      marketing: ready && sale && ask('marketingAllowed'),
      saleOfData: sale,
      heldBack: heldBack
    };
  }

  function apply() {
    var s = state();
    tools.forEach(function (t) {
      var ok = t.needs.every(function (k) { return s[k] === true; });
      try {
        if (ok && !t.running) { t.running = true; t.start(); }
        else if (!ok && t.running) { t.running = false; if (t.stop) t.stop(); }
      } catch (e) { /* one tool must never break the page or the other tools */ }
    });
    try { document.dispatchEvent(new CustomEvent('ws:consent', { detail: s })); } catch (e) {}
  }

  function register(name, needs, start, stop) {
    tools.push({ name: name, needs: needs || [], start: start, stop: stop, running: false });
    if (ready) apply();
  }

  /* Washington and Nevada: record "do not sell or share" for a visitor who has made no choice. Then check the tools. */
  function holdBack(done) {
    try {
      if (holdRegions.indexOf(region()) === -1 || typeof cp.setTrackingConsent !== 'function') return done();
      var now = typeof cp.currentVisitorConsent === 'function' ? cp.currentVisitorConsent() : null;
      if (!now || now.sale_of_data !== '') return done(); /* the visitor (or the browser signal) already chose: leave it */
      heldBack = true;
      cp.setTrackingConsent({ sale_of_data: false }, function () { done(); });
    } catch (e) { done(); }
  }

  function onApi() {
    cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return; /* fail closed */
    ready = true;
    holdBack(apply);
  }

  function start() {
    var S = window.Shopify;
    if (S && S.customerPrivacy) { onApi(); return; }
    if (S && typeof S.loadFeatures === 'function') {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) { if (!error) onApi(); });
    }
    /* No Shopify API on the page: fail closed. Nothing loads. */
  }

  document.addEventListener('visitorConsentCollected', function () {
    if (!ready) { cp = window.Shopify && window.Shopify.customerPrivacy; ready = !!cp; }
    apply();
  });

  window.wsConsent = { state: state, register: register };

  /* ---- Microsoft Clarity (the one tracking tool the theme loads) ---- */
  if (clarityId) {
    /* Not on a developer's local preview (127.0.0.1, localhost): test visits must not be recorded. window.wsConsentTest is set only by the automated test. */
    var local = /^(127\.0\.0\.1|localhost)$/.test(location.hostname) && !window.wsConsentTest;
    var injected = false, wanted = false;
    var inject = function () {
      if (injected || !wanted) return;
      injected = true;
      window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.clarity.ms/tag/' + encodeURIComponent(clarityId);
      document.head.appendChild(s);
    };
    var whenIdle = function () {
      if ('requestIdleCallback' in window) requestIdleCallback(inject, { timeout: 4000 }); else setTimeout(inject, 2000);
    };
    if (!local) {
      register('clarity', ['analytics', 'saleOfData'], function () {
        wanted = true;
        if (injected) return; /* stopped earlier on this page: it starts again on the next page */
        if (document.readyState === 'complete') whenIdle(); else window.addEventListener('load', whenIdle, { once: true });
      }, function () {
        wanted = false;
        if (!injected || typeof window.clarity !== 'function') return;
        try { window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' }); } catch (e) {}
        try { window.clarity('consent', false); } catch (e) {}
        try { window.clarity('stop'); } catch (e) {}
      });
    }
  }

  start();
})();
